import { actionIdOf, directPath, type UxActionBinding } from "./model"
import { asString, exactText, isRecord } from "./json"
import type { WidgetDefinitionSet } from "./model"
import type { Notice } from "./messages"

/** Shell-owned submission copy stays semantic until the result is rendered. */
export type SubmissionNotice = Extract<Notice, { key: string }>

export interface SubmitInputs {
  readonly draft: Readonly<Record<string, unknown>>
  /** The record actually at the invocation site, with the binding it was read through. */
  readonly record: { readonly bindingId: string; readonly record: { readonly key: string; readonly values: unknown; readonly version: unknown } } | null
  readonly context: Readonly<Record<string, unknown>>
}

/**
 * The declared action and its input, built only from direct input paths. Any mapping that is a ZEN
 * expression needs Norn's evaluator (#3210); submission is then unavailable rather than approximated.
 */
export function buildActionInput(binding: UxActionBinding, set: WidgetDefinitionSet, inputs: SubmitInputs): { actionId: string; input: Record<string, unknown> } | { unavailable: SubmissionNotice } {
  const actionId = actionIdOf(set, binding.action)
  if (!actionId) return { unavailable: { key: "action.notInstalled", values: { actionId: binding.action.id } } }
  const input: Record<string, unknown> = {}
  for (const [name, expression] of Object.entries(binding.inputMapping)) {
    const path = directPath(String(expression))
    // Canonical string literals are useful for create-version sentinels such as "0".
    // They are data, never executable expressions.
    let literal: string | undefined
    if (!path) {
      try {
        const parsed: unknown = JSON.parse(String(expression))
        if (parsed === "0") literal = parsed
      } catch {
        // ZEN expressions remain unavailable in the renderer shell.
      }
      if (literal === undefined) return { unavailable: { key: "action.inputExpression", values: { name }, issue: 3210 } }
    }
    if (path?.namespace === "selection") return { unavailable: { key: "action.inputSelection", values: { name } } }
    // Record members and the runtime key come only from the declared binding's record at this site.
    if (path?.namespace === "record" || path?.namespace === "recordKey" || path?.namespace === "recordVersion") {
      const declared = binding.inputs.recordBindingId
      if (!declared) return { unavailable: { key: "action.inputRecordUndeclared", values: { name } } }
      if (!inputs.record || inputs.record.bindingId !== declared) return { unavailable: { key: "action.inputRecordUnavailable", values: { name, bindingId: declared } } }
    }
    const value = literal ?? (
      path?.namespace === "draft"
        ? inputs.draft[path.name]
        : path?.namespace === "record"
          ? (inputs.record?.record.values as Record<string, unknown> | undefined)?.[path.name]
          : path?.namespace === "recordKey"
            ? inputs.record?.record.key
            : path?.namespace === "recordVersion"
              ? canonicalVersion(inputs.record?.record.version)
              : path?.namespace === "context"
                ? inputs.context[path.name]
                : undefined
    )
    if (path?.namespace === "recordVersion" && value === undefined) {
      return { unavailable: { key: "action.inputRecordVersion", values: { name } } }
    }
    // Optional draft inputs that are absent are omitted, never sent as null (UX contract).
    if (value === undefined || value === "") continue
    input[name] = value
  }
  const parentValues = parentValuesDraftInput(binding, set)
  if (
    parentValues &&
    inputs.draft[parentValues.draftName] === undefined &&
    !Object.hasOwn(input, parentValues.inputName)
  ) {
    input[parentValues.inputName] = {}
  }
  return { actionId, input }
}

/**
 * A row-mutation action always consumes a parent values object, even when its form has no parent
 * property fields. Default only the exact direct-draft mapping declared by the installed effect.
 */
function parentValuesDraftInput(
  binding: UxActionBinding,
  set: WidgetDefinitionSet,
): { inputName: string; draftName: string } | undefined {
  const action = set.artifacts.find((candidate) =>
    candidate.kind === "action" && candidate.artifactId === binding.action.id && candidate.version === binding.action.version,
  )
  const effect = action?.payload.effectProgram
  if (!isRecord(effect) || effect.kind !== "mutate_parent_with_rows") return undefined
  const inputName = asString(effect.parentValuesField)
  if (!inputName || !Object.hasOwn(binding.inputMapping, inputName)) return undefined
  const path = directPath(String(binding.inputMapping[inputName]))
  return path?.namespace === "draft" ? { inputName, draftName: path.name } : undefined
}

export function canonicalVersion(value: unknown): string | undefined {
  const text = exactText(value) ?? (typeof value === "number" && Number.isSafeInteger(value) ? String(value) : undefined)
  if (!text || !/^(0|[1-9]\d*)$/.test(text)) return undefined
  try {
    return BigInt(text) <= 18_446_744_073_709_551_615n ? text : undefined
  } catch {
    return undefined
  }
}
